import * as React from 'react';
import classnames from 'classnames';
import { WithThemeProps } from '../theme';
import { ThemeConsumer } from '../../theme';
import { CdnIcon } from '@shared/ui';

export interface SliderProps {
	alwaysShowArrows?: boolean;

	/**
	 * Slider step
	 */
	offset?: number;

	/**
	 * Number of centered item.
	 * Will be ignored if `leftStartItem` passed.
	 */
	centeredItem?: number;

	/**
	 * Number of visible begin item
	 */
	leftStartItem?: number;

	/**
	 * Min width of item
	 */
	minItemWidth?: number;

	/**
	 * User can slide items by finger moving
	 */
	touchEvents?: boolean;

	touchTransition?: 'default' | 'by-items';

	/**
	 * Max visible items
	 */
	maxItemsCount?: number;

	/**
	 * Do not display left arrow when first element is visible
	 *
	 * @default false
	 */
	hideDisabledLeftArrow?: boolean;

	/**
	 * Do not display right arrow when last element is visible
	 *
	 * @default false
	 */
	hideDisabledRightArrow?: boolean;

	/**
	 * Disable left arrow when first element is visible instead of hiding it
	 *
	 * @default false
	 */
	disabledLeftArrow?: boolean;

	/**
	 * Disable right arrow when last element is visible instead of hiding it
	 *
	 * @default false
	 */
	disabledRightArrow?: boolean;
	innerRef?: React.LegacyRef<HTMLDivElement>;
	wrapperClassName?: string;
	onRangeChange?: (
		direction: 'next' | 'prev',
		startItemIndex: number,
		itemsCount: number,
		itemsOutsideBoundary: number
	) => void;

	/**
	 * Event when component set centered item (when passed centeredItem property only)
	 * @param {number} startItemIndex
	 * @param {number} itemsCount
	 * @param {number} itemsOutsideBoundary
	 */
	onItemCentered?: (startItemIndex: number, itemsCount: number, itemsOutsideBoundary: number) => void;
	/**
	 * Prevents cumulative offset drift on tablet and mobile in PriceGraph
	 */
	useRealItemStep?: boolean;
}

class Slider extends React.Component<Partial<WithThemeProps> & SliderProps> {
	public static displayName = 'Slider';
	private sliderInsideRef: HTMLElement = null;
	private sliderRef: HTMLElement = null;
	private leftArrowRef: HTMLElement = null;
	private rightArrowRef: HTMLElement = null;
	private sliderOffset = 0;
	private sliderWidth = 0;
	private sliderWrapperWidth = 0;
	private itemWidth = 1;
	private itemsCount = 0;
	private visibleItemsCount = 0;
	private touchInitX = 0;
	private touchBegin = false;

	constructor(props: Partial<WithThemeProps> & SliderProps) {
		super(props);

		this.itemsBeyondRightEdge = this.itemsBeyondRightEdge.bind(this);
		this.itemsBeyondLeftEdge = this.itemsBeyondLeftEdge.bind(this);
		this.startTouch = this.startTouch.bind(this);
		this.endTouch = this.endTouch.bind(this);
		this.touchMove = this.touchMove.bind(this);
		this.screenResize = this.screenResize.bind(this);
		this.slideRight = this.slideRight.bind(this);
		this.slideLeft = this.slideLeft.bind(this);
	}

	/**
	 * Get items count on beyond the right edge
	 */
	public itemsBeyondRightEdge() {
		return Math.round((this.sliderWrapperWidth + (this.sliderOffset - this.sliderWidth)) / this.itemWidth);
	}

	/**
	 * Get items count on beyond the left edge
	 */
	public itemsBeyondLeftEdge() {
		return Math.round(Math.abs(this.sliderOffset / this.itemWidth));
	}

	public calculateSizes() {
		if (this.sliderRef && this.sliderInsideRef) {
			this.sliderWidth = this.sliderRef.offsetWidth;
			this.sliderWrapperWidth = this.sliderInsideRef.offsetWidth;

			const wrapper = this.sliderInsideRef.childNodes[0] as HTMLElement | null;

			if (this.props.maxItemsCount) {
				this.itemWidth = this.calculateItemWidth(this.props.maxItemsCount);
			} else {
				this.itemWidth = this.sliderInsideRef.offsetWidth / this.itemsCount;
			}

			if (this.props.useRealItemStep && wrapper) {
				this.itemsCount = wrapper.childNodes.length;
				this.sliderWrapperWidth = wrapper.scrollWidth;

				const first = wrapper.childNodes[0] as HTMLElement | undefined;
				const second = wrapper.childNodes[1] as HTMLElement | undefined;

				if (first && second) {
					const r1 = first.getBoundingClientRect();
					const r2 = second.getBoundingClientRect();
					const step = r2.left - r1.left;
					if (step > 0) {
						this.itemWidth = step;
					}
				}
			}

			this.visibleItemsCount = Math.round(this.sliderWidth / this.itemWidth);
		}
	}

	// Touch events
	public startTouch(e: any) {
		const {
			Slider: { SliderTheme: theme }
		} = this.props.theme;
		const inside = theme?.inside_transition?.split(' ') ?? [];

		this.touchBegin = true;

		this.sliderInsideRef.classList.remove(...inside);

		this.touchInitX = e.touches[0].clientX;
	}

	public endTouch() {
		const {
			Slider: { SliderTheme: theme }
		} = this.props.theme;

		const insideTransition = theme?.inside_transition?.split(' ') ?? [];

		this.touchBegin = false;

		this.sliderInsideRef.classList.add(...insideTransition);

		this.touchInitX = 0;

		this.offsetCorrection();
	}

	public touchMove(e: TouchEvent) {
		if (this.touchBegin) {
			e.preventDefault();

			const delta = e.touches[0].clientX - this.touchInitX;

			if (this.props.touchTransition === 'default') {
				this.setOffset(this.sliderOffset + delta);

				this.touchInitX = e.touches[0].clientX;
			} else if (this.props.touchTransition === 'by-items') {
				if (Math.abs(delta) > this.props.minItemWidth / 1.5) {
					delta > 0 ? this.slideLeft(1) : this.slideRight(1);

					this.touchInitX = e.touches[0].clientX;
				}
			}
		}
	}

	public offsetCorrection() {
		if (this.sliderOffset > 0) {
			this.setOffset(0);
		} else if (this.itemsBeyondRightEdge() < 0) {
			this.setOffset(this.sliderWidth - this.sliderWrapperWidth);
		}
	}

	public screenResize() {
		this.calculateSizes();

		if (this.props.leftStartItem) {
			this.setInLeftView(this.props.leftStartItem);
		} else if (this.props.centeredItem) {
			this.setInCenterView(this.props.centeredItem);
		}

		if (this.props.onItemCentered) {
			this.props.onItemCentered(
				Math.round(this.itemsBeyondLeftEdge()),
				this.visibleItemsCount,
				this.itemsBeyondRightEdge()
			);
		}
	}

	public calculateItemWidth(itemsCount: number): number {
		const itemSize = Math.round(this.sliderWidth / itemsCount);

		if (this.props.minItemWidth && itemSize < this.props.minItemWidth) {
			return this.calculateItemWidth(itemsCount - 1);
		}

		this.setItemsWidth(itemSize);

		return itemSize;
	}

	public setItemsWidth(size: number) {
		if (this.sliderInsideRef) {
			Array.from(this.sliderInsideRef.childNodes[0].childNodes).forEach(node => {
				(node as HTMLDivElement).style.width = `${size}px`;
			});
		}
	}

	public componentDidMount() {
		this.itemsCount = this.sliderInsideRef.childNodes[0].childNodes.length;

		this.calculateSizes();

		window.addEventListener('resize', this.screenResize);

		if (this.props.touchEvents && this.sliderRef) {
			this.sliderRef.addEventListener('touchstart', this.startTouch);
			this.sliderRef.addEventListener('touchmove', this.touchMove);
			this.sliderRef.addEventListener('touchend', this.endTouch);
		}

		this.screenResize();
		this.arrowAvailabilityCheck();
	}

	public componentDidUpdate() {
		if (this.props.leftStartItem >= 0) {
			this.setInLeftView(this.props.leftStartItem);
		} else if (this.props.centeredItem >= 0) {
			this.setInCenterView(this.props.centeredItem);
		}

		this.arrowAvailabilityCheck();
	}

	public slideRight(neededOffset?: number) {
		if (this.props.disabledRightArrow && this.itemsBeyondRightEdge() <= 0) {
			return;
		}

		const itemsCount = this.itemsBeyondRightEdge(),
			itemsOffset = neededOffset
				? neededOffset
				: this.props.offset
				? this.props.offset
				: Math.round(this.visibleItemsCount / 2);
		let offset = this.sliderOffset;

		if (this.sliderInsideRef && itemsCount > 0) {
			offset -= this.itemWidth * (itemsCount > itemsOffset ? itemsOffset : itemsCount);

			this.setOffset(offset);
		}

		if (this.props.onRangeChange) {
			this.props.onRangeChange(
				'next',
				Math.round(this.itemsBeyondLeftEdge()),
				this.visibleItemsCount,
				itemsCount
			);
		}
	}

	public slideLeft(neededOffset?: number) {
		if (this.props.disabledLeftArrow && this.itemsBeyondLeftEdge() <= 0) {
			return;
		}

		const itemsCount = this.itemsBeyondLeftEdge(),
			itemsOffset = neededOffset
				? neededOffset
				: this.props.offset
				? this.props.offset
				: Math.round(this.visibleItemsCount / 2);
		let offset = this.sliderOffset;

		if (this.sliderInsideRef && itemsCount > 0) {
			offset += this.itemWidth * (itemsCount > itemsOffset ? itemsOffset : itemsCount);

			this.setOffset(offset);
		}

		if (this.props.onRangeChange) {
			this.props.onRangeChange(
				'prev',
				Math.round(this.itemsBeyondLeftEdge()),
				this.visibleItemsCount,
				itemsCount
			);
		}
	}

	/**
	 * Set item in center of view
	 * @param {number} item
	 */
	public setInCenterView(item: number) {
		const itemsCountInView = Math.round(this.sliderWidth / this.itemWidth),
			itemsHalfCountInView = Math.round(itemsCountInView / 2),
			itemsCountBefore = item - itemsHalfCountInView;

		let offset = 0;

		if (itemsCountBefore > 0) {
			offset = itemsCountBefore;

			if (this.itemsCount - offset < itemsCountInView) {
				offset = this.itemsCount - itemsCountInView;
			}
		}

		this.setOffset(offset * this.itemWidth * -1);
	}

	public setInLeftView(item: number) {
		this.setOffset(item * this.itemWidth * -1);
	}

	public setOffset(offset: number) {
		if (this.sliderInsideRef) {
			this.sliderInsideRef.style.left = `${offset}px`;
		}

		this.sliderOffset = offset;

		this.arrowAvailabilityCheck();
	}

	public arrowAvailabilityCheck() {
		const {
			Slider: { SliderTheme: theme }
		} = this.props.theme;

		const hide = theme?.hide?.split(' ') ?? [];
		const disabled = theme?.disabled?.split(' ') ?? [];

		if (!this.props.alwaysShowArrows) {
			const leftEdgeReached = this.itemsBeyondLeftEdge() <= 0;
			const rightEdgeReached = this.itemsBeyondRightEdge() <= 0;

			if (this.props.hideDisabledLeftArrow && leftEdgeReached) {
				this.leftArrowRef.classList.add(...hide);
			} else {
				this.leftArrowRef.classList.remove(...hide);
			}

			if (this.props.disabledLeftArrow && leftEdgeReached) {
				this.leftArrowRef.classList.add(...disabled);
			} else {
				this.leftArrowRef.classList.remove(...disabled);
			}

			if (this.props.hideDisabledRightArrow && rightEdgeReached) {
				this.rightArrowRef.classList.add(...hide);
			} else {
				this.rightArrowRef.classList.remove(...hide);
			}

			if (this.props.disabledRightArrow && rightEdgeReached) {
				this.rightArrowRef.classList.add(...disabled);
			} else {
				this.rightArrowRef.classList.remove(...disabled);
			}
		}
	}

	public componentWillUnmount() {
		window.removeEventListener('resize', this.screenResize);

		if (this.props.touchEvents && this.sliderRef) {
			this.sliderRef.removeEventListener('touchstart', this.startTouch);
			this.sliderRef.removeEventListener('touchmove', this.touchMove);
			this.sliderRef.removeEventListener('touchend', this.endTouch);
		}
	}

	public render(): React.ReactNode {
		const {
			Slider: { SliderTheme: theme }
		} = this.props.theme;

		return (
			<div className={theme.container}>
				<div
					className={classnames(theme.arrow, theme.arrow_next)}
					ref={ref => (this.rightArrowRef = ref)}
					onClick={() => this.slideRight()}
				>
					<CdnIcon icon="Arrow/Chevron_right" />
				</div>

				<div
					className={classnames(theme.arrow, theme.arrow_prev)}
					ref={ref => (this.leftArrowRef = ref)}
					onClick={() => this.slideLeft()}
				>
					<CdnIcon icon="Arrow/Chevron_left" />
				</div>

				<div ref={ref => (this.sliderRef = ref)} className={theme.slider}>
					<div
						ref={ref => (this.sliderInsideRef = ref)}
						className={classnames(theme.inside, theme.inside_transition)}
					>
						<div
							ref={this.props.innerRef}
							className={classnames(theme.elementsWrapper, this.props.wrapperClassName)}
						>
							{this.props.children}
						</div>
					</div>
				</div>
			</div>
		);
	}
}

const SliderWithTheme: React.FC<SliderProps> = props => (
	<ThemeConsumer>{theme => <Slider {...props} theme={theme} />}</ThemeConsumer>
);
SliderWithTheme.displayName = 'Slider';

export default SliderWithTheme;
